import axios from 'axios';

const host = typeof window !== 'undefined' ? window.location.hostname : '';
const isTestHost =
  host === 'test.brandsearcher.cc' ||
  host === 'localhost' ||
  host === '127.0.0.1' ||
  host.endsWith('.brandsearcher.pages.dev');
const backendBase = isTestHost
  ? ''
  : (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${backendBase}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (redirect to login)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Auth
  adminLogin: (username, password) => api.post('/auth/admin', { username, password }),

  // Dashboard
  getDashboardStats: (days = 14, geo = null) => api.get('/dashboard/stats', { params: { days, geo } }),
  
  // Entities
  getTopSpikes: (geo = null, limit = 50, skip = 0, entity_type = null, days = 14, filters = {}, scope = 'spikes') => 
    api.get('/entities/top-spikes', { params: { geo, limit, skip, entity_type, days, scope, ...filters } }),
  
  getEntityDetails: (entityId) => api.get(`/entities/${entityId}`),
  
  getEntityTimeline: (entityId, geo = null, days = 90) => 
    api.get(`/entities/${entityId}/timeline`, { params: { geo, days } }),
  getEntitiesList: (
    geo = null,
    days = 14,
    verification = 'verified',
    mode = 'active',
    sort_key = 'score',
    sort_dir = 'desc',
    limit = 100,
    skip = 0,
    filters = {}
  ) =>
    api.get('/entities/list', { params: { geo, days, verification, mode, sort_key, sort_dir, limit, skip, ...filters } }),
  
  // Search
  searchEntities: (q, geo = null, limit = 100, skip = 0, verification = 'all', timeoutMs = 30000) =>
    api.get('/entities/search', { params: { q, geo, limit, skip, verification }, timeout: timeoutMs }),
  checkBrand: (name, geos = null) => api.post('/brand/check', { name, geos }),
  
  // Budget
  getBudgetStatus: () => api.get('/budget/status'),
  
  // GEOs
  getAvailableGeos: () => api.get('/geos'),
  
  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.post('/settings', data),
  
  // Pipeline
  runPipeline: () => api.post('/pipeline/run'),
  runDailyQualityRun: () => api.post('/pipeline/daily-quality-run'),
  runSavedFiltersAlerts: () => api.post('/pipeline/saved-filters-alerts-run'),
  runBackfill: (days = 14) => api.post(`/pipeline/backfill?days=${days}`),
  runDetectSpikes: () => api.post('/pipeline/detect-spikes'),
  runReviewSignals: (
    limit = 800,
    max_serp_snapshots_per_entity = 3,
    min_days_between_checks = 3,
    require_review_pattern = true,
    dry_run = false
  ) =>
    api.post('/pipeline/review-signals-run', null, {
      params: {
        limit,
        max_serp_snapshots_per_entity,
        min_days_between_checks,
        require_review_pattern,
        dry_run
      }
    }),
  runReprocess: () => api.post('/pipeline/reprocess'),
  runLicenseCheck: () => api.post('/pipeline/license-check'),
  runUnconfirmedCleanupSync: (max_scan = 5000) => api.post('/pipeline/unconfirmed-cleanup', null, { params: { max_scan } }),
  runUnconfirmedScanDeleteNow: (max_scan = 5000, max_delete = 500, dry_run = false) =>
    api.post('/pipeline/unconfirmed-cleanup/scan-delete-now', null, { params: { max_scan, max_delete, dry_run } }),
  getUnconfirmedCleanupCandidates: (status = 'pending_review', limit = 200) =>
    api.get('/pipeline/unconfirmed-cleanup/candidates', { params: { status, limit } }),
  decideUnconfirmedCleanupCandidates: (entity_ids, decision) =>
    api.post('/pipeline/unconfirmed-cleanup/decide', { entity_ids, decision }),
  applyApprovedCleanupDeletions: (dry_run = false, max_delete = 200, force = false) =>
    api.post('/pipeline/unconfirmed-cleanup/apply', null, { params: { dry_run, max_delete, force } }),
  runSerpSegments: (geos) => api.post('/pipeline/serp-segments', geos ? { geos } : {}),
  runSerpSegmentsIncremental: () => api.post('/pipeline/serp-segments-incremental'),
  runWatchlistCollect: () => api.post('/pipeline/watchlist-collect'),
  cancelWatchlistCollect: () => api.post('/pipeline/watchlist-collect/cancel'),
  getWatchlistCollectStatus: () => api.get('/pipeline/watchlist-collect/status'),
  getSerpSegmentScores: (geo, status) => api.get('/serp-segments/scores', { params: { geo, status } }),
  getSerpSegmentHistory: (brand, geo) => api.get(`/serp-segments/${encodeURIComponent(brand)}`, { params: { geo } }),
  getPipelineStatus: () => api.get('/pipeline/status'),
  getPipelineControlStatus: () => api.get('/pipeline/control/status'),
  pausePipeline: () => api.post('/pipeline/control/pause'),
  resumePipeline: () => api.post('/pipeline/control/resume'),
  stopPipeline: () => api.post('/pipeline/control/stop'),
  resetPipeline: () => api.post('/pipeline/control/reset'),
  getSchedule: () => api.get('/schedule'),
  
  // Watchlist
  getWatchlist: (geo, days) => api.get('/watchlist', { params: { ...(geo ? { geo } : {}), ...(days ? { days } : {}) } }),
  getWatchlistAnalytics: (geo = null, days = 7) => api.get('/watchlist/analytics', { params: { geo, days } }),
  getWatchlistCompare: (entity_ids, geo, days = 14) => api.get('/watchlist/compare', { params: { entity_ids: entity_ids.join(','), geo, days } }),
  getWatchlistCompareGeos: (entity_ids, days = 14) => api.get('/watchlist/compare-geos', { params: { entity_ids: entity_ids.join(','), days } }),
  addToWatchlist: (entity_id, geos = []) => api.post('/watchlist/add', { entity_id, geos }),
  removeFromWatchlist: (entity_id) => api.post('/watchlist/remove', { entity_id }),
  
  // SERP / DataForSEO
  fetchSerp: (brand_name, geo, entity_id = null, niche = 'casino') => api.post('/serp/fetch', { brand_name, geo, entity_id, niche }),
  getSerpSnapshots: (entityId, geo = null) => api.get(`/serp/${entityId}`, { params: { geo } }),
  getLatestSerp: (entityId, geo = null) => api.get(`/serp/${entityId}/latest`, { params: { geo } }),
  testDataForSEO: () => api.post('/dataforseo/test'),
  getDataForSEOBudget: () => api.get('/dataforseo/budget'),
  classifyAll: (force = false) => api.post('/classify/all', { force }),
  classifyEntity: (entityId) => api.post(`/classify/${entityId}`),
  verifyAll: () => api.post('/verify-all'),
  runAiFallbackVerify: (
    limit = 300,
    dry_run = false,
    from_cleanup_queue = true,
    auto_blacklist_noise = true,
    force_ai_for_queue = true,
    auto_keep_positive = true
  ) =>
    api.post('/ai-fallback/verify-run', null, {
      params: { limit, dry_run, from_cleanup_queue, auto_blacklist_noise, force_ai_for_queue, auto_keep_positive }
    }),
  runAiFallbackVerifyEntity: (entity_id, auto_blacklist_noise = true, auto_keep_positive = true) =>
    api.post(`/ai-fallback/verify-entity/${entity_id}`, null, {
      params: { auto_blacklist_noise, auto_keep_positive }
    }),
  getAiFallbackStatus: () => api.get('/ai-fallback/status'),
  verifyEntity: (entityId) => api.post(`/verify/${entityId}`),
  serpCheckAll: () => api.post('/serp/check-all'),
  ahrefsCrossGeo: (params = {}) => api.post('/ahrefs/cross-geo', null, { params }),
  ahrefsDiscover: (params = {}) => api.post('/ahrefs/discover', null, { params }),
  ahrefsIngest: () => api.post('/ahrefs/ingest'),
  getAhrefsRunRecommendation: () => api.get('/ahrefs/run-recommendation'),
  searchapiCollect: () => api.post('/pipeline/searchapi-collect'),
  verifyDomains: () => api.post('/pipeline/verify-domains'),
  pbnAnalyzeAll: () => api.post('/pbn/analyze-all'),
  pbnAnalyze: (entityId) => api.post(`/pbn/analyze/${entityId}`),
  pbnGet: (entityId) => api.get(`/pbn/${entityId}`),
  getAffiliateMetrics: (entityId, geo = null) => api.get(`/affiliate-metrics/${entityId}`, { params: { geo } }),
  
  // User management (admin)
  getUsers: () => api.get('/users'),
  getAdminActivity: (params = {}) => api.get('/admin/activity', { params }),
  getAdminSessions: () => api.get('/auth/admin/sessions'),
  revokeAdminSession: (sessionId) => api.post(`/auth/admin/sessions/${encodeURIComponent(sessionId)}/revoke`),
  approveUser: (email) => api.post(`/users/${encodeURIComponent(email)}/approve`),
  disableUser: (email) => api.post(`/users/${encodeURIComponent(email)}/disable`),
  deleteUser: (email) => api.delete(`/users/${encodeURIComponent(email)}`),
  updateUser: (email, data) => api.put(`/users/${encodeURIComponent(email)}`, data),

  // User profile
  getMe: () => api.get('/auth/me'),
  getMySessions: () => api.get('/auth/me/sessions'),
  revokeMySession: (sessionId) => api.post(`/auth/me/sessions/${encodeURIComponent(sessionId)}/revoke`),
  updateMyGeos: (geos) => api.put('/auth/me/geos', { geos }),
  getMyApiKeys: () => api.get('/auth/me/api-keys'),
  revealApiKey: (field) => api.get(`/auth/me/api-keys/reveal/${field}`),
  updateMyApiKeys: (keys) => api.put('/auth/me/api-keys', keys),
  getMyBudget: () => api.get('/auth/me/budget'),
  deleteMyAccount: () => api.delete('/auth/me'),

  // Alerts / tracked filters
  createTrackedFilter: (data) => api.post('/alerts/filters', data),
  getTrackedFilters: () => api.get('/alerts/filters'),
  updateTrackedFilter: (id, data) => api.put(`/alerts/filters/${id}`, data),
  deleteTrackedFilter: (id) => api.delete(`/alerts/filters/${id}`),
  sendTrackedFilterTestEmail: (id) => api.post(`/alerts/filters/${id}/test-email`),
  getTrackedFilterHistory: (id, limit = 30) => api.get(`/alerts/filters/${id}/history`, { params: { limit } }),
  getUnsubscribeOptions: (token, filter_id) => api.get('/alerts/unsubscribe/options', { params: { token, filter_id } }),
  applyUnsubscribe: (token, scope, filter_id = null) => api.post('/alerts/unsubscribe', { token, scope, filter_id }),

  // Demo data
  initializeDemoData: () => api.post('/init-demo-data'),
};

export default api;
